import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  Plus, 
  Search, 
  Pencil, 
  Trash2, 
  BarChart3, 
  Tag
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  listCoupons, 
  createCoupon, 
  updateCoupon, 
  deleteCoupon,
  getCouponAnalytics,
  getCouponAuditLog
} from "@/lib/marketing/coupons.functions";
import { formatISTDateTime } from "@/lib/date/ist";
import { couponInput } from "@/lib/admin/meal-plans-v2.schemas";
import { couponDateForInput, couponDateFromInput } from "@/lib/admin/coupon-fields";
import { formatPaise } from "@/lib/format/money";
import {
  getHomepageCouponSettings,
  updateHomepageCouponSettings,
  getCheckoutCouponSettings,
  updateCheckoutCouponSettings,
} from "@/lib/marketing/homepage-coupon.functions";

export const Route = createFileRoute("/_admin/admin/coupons")({
  head: () => ({ meta: [
    { title: "Coupon Management | Poonam Kapur Healthy Kitchen" },
    { name: "description", content: "Manage Healthy Kitchen coupon validity, discount limits and promotional displays." },
    { property: "og:title", content: "Coupon Management | Poonam Kapur Healthy Kitchen" },
    { property: "og:description", content: "Manage Healthy Kitchen coupon validity, discount limits and promotional displays." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: CouponsAdminPage,
});

function CouponsAdminPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [analyticsId, setAnalyticsId] = useState<string | null>(null);

  const coupons = useQuery({
    queryKey: ["admin", "coupons"],
    queryFn: () => listCoupons(),
  });

  const mCreate = useMutation({
    mutationFn: (data: any) => createCoupon({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      toast.success("Coupon created");
      setEditingCoupon(null);
    },
    onError: (e: any) => toast.error(e.message || "Failed to create coupon"),
  });

  const mUpdate = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => updateCoupon({ data: { id, patch } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      toast.success("Coupon updated");
      setEditingCoupon(null);
    },
    onError: (e: any) => toast.error(e.message || "Failed to update coupon"),
  });

  const mDelete = useMutation({
    mutationFn: (id: string) => deleteCoupon({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
      toast.success("Coupon deleted");
      setDeletingId(null);
    },
    onError: (e: any) => toast.error(e.message || "Failed to delete coupon"),
  });

  const filtered = (coupons.data || []).filter((c: any) => 
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Coupon Management"
        description="Create and manage discount coupons for subscription plans."
        actions={
          <Button onClick={() => setEditingCoupon({ code: "", name: "", type: "percentage", value: 0, is_active: true })}>
            <Plus className="mr-2 h-4 w-4" /> New Coupon
          </Button>
        }
      />

      <HomepageCouponDisplayControl coupons={coupons.data || []} />
      <CheckoutCouponDisplayControl coupons={coupons.data || []} />

      <AdminDataToolbar
        moduleId="coupons"
        exportName="Coupons"
        rows={() => (coupons.data || []).map((c: any) => ({
          Code: c.code,
          Name: c.name,
          Discount: c.value + (c.type === "percentage" ? "%" : " Paise"),
          Active: c.is_active ? "Yes" : "No",
        }))}
        onImported={() => queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] })}
      />


      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search coupons..."
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code / Name</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((coupon: any) => (
              <TableRow key={coupon.id}>
                <TableCell>
                  <div className="font-mono font-bold text-primary">{coupon.code}</div>
                  <div className="text-sm font-medium">{coupon.name}</div>
                  <div className="text-xs text-muted-foreground">{coupon.description}</div>
                </TableCell>
                <TableCell>
                  {coupon.type === "percentage" ? (
                    <Badge variant="outline">{coupon.value}% OFF</Badge>
                  ) : (
                    <Badge variant="outline">{formatPaise(coupon.value)} OFF</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-xs">
                    {coupon.start_at ? formatISTDateTime(coupon.start_at) : "Always"}
                    {" → "}
                    {coupon.end_at ? formatISTDateTime(coupon.end_at) : "Never"}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-xs">
                    Per Customer: {coupon.max_uses_per_customer || 1}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={coupon.is_active ? "default" : "secondary"}>
                    {coupon.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="icon" onClick={() => setAnalyticsId(coupon.id)}>
                      <BarChart3 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEditingCoupon(coupon)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeletingId(coupon.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingCoupon} onOpenChange={(o) => !o && setEditingCoupon(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCoupon?.id ? "Edit Coupon" : "Create Coupon"}</DialogTitle>
          </DialogHeader>
          {editingCoupon && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="code">Coupon Code</Label>
                <Input 
                  id="code" 
                  value={editingCoupon.code} 
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, code: e.target.value.toUpperCase() })} 
                  placeholder="SAVE20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Coupon Name</Label>
                <Input 
                  id="name" 
                  value={editingCoupon.name} 
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, name: e.target.value })} 
                  placeholder="Welcome Discount"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Discount Type</Label>
                <Select 
                  value={editingCoupon.type} 
                  onValueChange={(v) => setEditingCoupon({ ...editingCoupon, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">
                  {editingCoupon.type === "percentage" ? "Discount Percentage" : "Discount Amount (in Paise)"}
                </Label>
                <Input 
                  id="value" 
                  type="number"
                  value={editingCoupon.value} 
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, value: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_total_uses">Max Total Uses</Label>
                <Input 
                  id="max_total_uses" 
                  type="number"
                  placeholder="Leave empty for unlimited"
                  value={editingCoupon.max_total_uses || ""} 
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, max_total_uses: e.target.value ? parseInt(e.target.value) : null })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_uses_per_customer">Max Uses Per Customer</Label>
                <Input 
                  id="max_uses_per_customer" 
                  type="number"
                  value={editingCoupon.max_uses_per_customer || 1} 
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, max_uses_per_customer: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_order_paise">Minimum Order Amount (₹) — optional</Label>
                <Input
                  id="min_order_paise"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Leave blank for no minimum"
                  value={editingCoupon.min_order_paise != null ? editingCoupon.min_order_paise / 100 : ""}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, min_order_paise: e.target.value === "" ? null : Math.round(parseFloat(e.target.value) * 100) })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_discount_paise">Maximum Discount (₹) — optional</Label>
                <Input
                  id="max_discount_paise"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Leave blank for no cap"
                  value={editingCoupon.max_discount_paise != null ? editingCoupon.max_discount_paise / 100 : ""}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, max_discount_paise: e.target.value === "" ? null : Math.round(parseFloat(e.target.value) * 100) })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_at">Valid From</Label>
                <Input 
                  id="start_at" 
                  type="datetime-local"
                  value={couponDateForInput(editingCoupon.start_at)}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, start_at: couponDateFromInput(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_at">Valid Until</Label>
                <Input 
                  id="end_at" 
                  type="datetime-local"
                  value={couponDateForInput(editingCoupon.end_at)}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, end_at: couponDateFromInput(e.target.value) })}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={editingCoupon.description || ""} 
                  onChange={(e: any) => setEditingCoupon({ ...editingCoupon, description: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  id="is_active" 
                  checked={editingCoupon.is_active} 
                  onCheckedChange={(c) => setEditingCoupon({ ...editingCoupon, is_active: c })} 
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCoupon(null)}>Cancel</Button>
            <Button onClick={() => {
              const parsed = couponInput.safeParse(editingCoupon);
              if (!parsed.success) {
                toast.error(parsed.error.issues[0]?.message ?? "Please check the coupon fields");
                return;
              }
              if (editingCoupon.id) {
                mUpdate.mutate({ id: editingCoupon.id, patch: parsed.data });
              } else {
                mCreate.mutate(parsed.data);
              }
            }} disabled={mCreate.isPending || mUpdate.isPending}>
              {editingCoupon?.id ? "Save Changes" : "Create Coupon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!analyticsId} onOpenChange={(o) => !o && setAnalyticsId(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Coupon Analytics & Logs</DialogTitle>
          </DialogHeader>
          {analyticsId && <CouponAnalyticsView couponId={analyticsId} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the coupon. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletingId && mDelete.mutate(deletingId)} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function HomepageCouponDisplayControl({ coupons }: { coupons: any[] }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "homepage-coupon-display"],
    queryFn: () => getHomepageCouponSettings(),
  });

  const mSave = useMutation({
    mutationFn: (patch: { enabled: boolean; mode: "featured" | "rotation"; featured_coupon_id: string | null }) =>
      updateHomepageCouponSettings({ data: patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "homepage-coupon-display"] });
      queryClient.invalidateQueries({ queryKey: ["homepage", "coupon-display"] });
      toast.success("Homepage coupon display updated");
    },
    onError: (e: any) => toast.error(e.message || "Failed to update display setting"),
  });

  if (!settings.data) return null;
  const s = settings.data;
  const save = (patch: Partial<typeof s>) =>
    mSave.mutate({
      enabled: patch.enabled ?? s.enabled,
      mode: (patch.mode ?? s.mode) as "featured" | "rotation",
      featured_coupon_id: patch.featured_coupon_id !== undefined ? patch.featured_coupon_id : s.featured_coupon_id,
    });

  const activeCoupons = coupons.filter((c: any) => c.is_active && !c.is_demo);

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <Switch
            id="hp-coupon-enabled"
            checked={s.enabled}
            disabled={mSave.isPending}
            onCheckedChange={(v) => save({ enabled: v })}
          />
          <Label htmlFor="hp-coupon-enabled" className="font-medium">
            Homepage Coupon Display
          </Label>
        </div>
        {s.enabled && (
          <>
            <div className="flex items-center gap-2">
              <Label htmlFor="hp-coupon-mode" className="text-sm text-muted-foreground">
                Mode
              </Label>
              <Select value={s.mode} onValueChange={(v) => save({ mode: v as "featured" | "rotation" })}>
                <SelectTrigger id="hp-coupon-mode" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="rotation">Rotation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {s.mode === "featured" && (
              <div className="flex items-center gap-2">
                <Label htmlFor="hp-coupon-featured" className="text-sm text-muted-foreground">
                  Coupon
                </Label>
                <Select
                  value={s.featured_coupon_id ?? ""}
                  onValueChange={(v) => save({ featured_coupon_id: v || null })}
                >
                  <SelectTrigger id="hp-coupon-featured" className="w-48">
                    <SelectValue placeholder="Select a coupon" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCoupons.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Shows a promotional strip below the homepage hero with the coupon code and a copy button. Turning this off never changes the coupons themselves; checkout stays authoritative for eligibility.
      </p>
    </div>
  );
}

function CheckoutCouponDisplayControl({ coupons }: { coupons: any[] }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "checkout-coupon-display"],
    queryFn: () => getCheckoutCouponSettings(),
  });

  const mSave = useMutation({
    mutationFn: (patch: {
      enabled: boolean;
      placement: "above_input" | "order_summary";
      mode: "featured" | "rotation";
      featured_coupon_id: string | null;
    }) => updateCheckoutCouponSettings({ data: patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "checkout-coupon-display"] });
      queryClient.invalidateQueries({ queryKey: ["checkout", "coupon-display"] });
      toast.success("Checkout coupon display updated");
    },
    onError: (e: any) => toast.error(e.message || "Failed to update display setting"),
  });

  if (!settings.data) return null;
  const s = settings.data;
  const save = (patch: Partial<typeof s>) =>
    mSave.mutate({
      enabled: patch.enabled ?? s.enabled,
      placement: (patch.placement ?? s.placement) as "above_input" | "order_summary",
      mode: (patch.mode ?? s.mode) as "featured" | "rotation",
      featured_coupon_id: patch.featured_coupon_id !== undefined ? patch.featured_coupon_id : s.featured_coupon_id,
    });

  const activeCoupons = coupons.filter((c: any) => c.is_active && !c.is_demo);

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <Switch
            id="co-coupon-enabled"
            checked={s.enabled}
            disabled={mSave.isPending}
            onCheckedChange={(v) => save({ enabled: v })}
          />
          <Label htmlFor="co-coupon-enabled" className="font-medium">
            Checkout Coupon Display
          </Label>
        </div>
        {s.enabled && (
          <>
            <div className="flex items-center gap-2">
              <Label htmlFor="co-coupon-placement" className="text-sm text-muted-foreground">
                Placement
              </Label>
              <Select
                value={s.placement}
                onValueChange={(v) => save({ placement: v as "above_input" | "order_summary" })}
              >
                <SelectTrigger id="co-coupon-placement" className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="above_input">Above coupon input</SelectItem>
                  <SelectItem value="order_summary">Order summary panel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="co-coupon-mode" className="text-sm text-muted-foreground">
                Mode
              </Label>
              <Select value={s.mode} onValueChange={(v) => save({ mode: v as "featured" | "rotation" })}>
                <SelectTrigger id="co-coupon-mode" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="rotation">Rotation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {s.mode === "featured" && (
              <div className="flex items-center gap-2">
                <Label htmlFor="co-coupon-featured" className="text-sm text-muted-foreground">
                  Coupon
                </Label>
                <Select
                  value={s.featured_coupon_id ?? ""}
                  onValueChange={(v) => save({ featured_coupon_id: v || null })}
                >
                  <SelectTrigger id="co-coupon-featured" className="w-48">
                    <SelectValue placeholder="Select a coupon" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCoupons.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.code} — {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Shows eligible offers on checkout with a copy-code action. Copying never applies a discount; the coupon input and server-side validation stay unchanged. Turning this off never changes the coupons themselves.
      </p>
    </div>
  );
}

function CouponAnalyticsView({ couponId }: { couponId: string }) {
  const analytics = useQuery({
    queryKey: ["admin", "coupons", couponId, "analytics"],
    queryFn: () => getCouponAnalytics({ data: { id: couponId } }),
  });

  const auditLog = useQuery({
    queryKey: ["admin", "coupons", couponId, "audit-log"],
    queryFn: () => getCouponAuditLog({ data: { id: couponId } }),
  });

  if (analytics.isLoading || auditLog.isLoading) return <div>Loading...</div>;

  const stats = analytics.data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 border rounded bg-muted/50">
          <div className="text-xs text-muted-foreground uppercase">Usage Count</div>
          <div className="text-2xl font-bold">{stats?.usage_count || 0}</div>
        </div>
        <div className="p-4 border rounded bg-muted/50">
          <div className="text-xs text-muted-foreground uppercase">Total Discounted</div>
          <div className="text-2xl font-bold">{formatPaise(stats?.total_discount_paise || 0)}</div>
        </div>
        <div className="p-4 border rounded bg-muted/50">
          <div className="text-xs text-muted-foreground uppercase">Unique Customers</div>
          <div className="text-2xl font-bold">{stats?.unique_customers || 0}</div>
        </div>
      </div>

      <Tabs defaultValue="audit">
        <TabsList>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
          <TabsTrigger value="redemptions">Recent Redemptions</TabsTrigger>
        </TabsList>
        <TabsContent value="audit" className="pt-4">
          <div className="space-y-4">
            {(auditLog.data as any[])?.map((log: any) => (
              <div key={log.id} className="text-sm border-b pb-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatISTDateTime(log.created_at)}</span>
                  <span>{log.action}</span>
                </div>
                <div className="mt-1">
                  <span className="font-semibold">{log.performed_by || "System"}</span>: {log.details}
                </div>
              </div>
            ))}
            {(auditLog.data as any[])?.length === 0 && <div className="text-center py-8 text-muted-foreground">No audit logs found.</div>}
          </div>
        </TabsContent>
        <TabsContent value="redemptions" className="pt-4">
          <div className="text-center py-8 text-muted-foreground">
            Redemption details are linked to customer orders.
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
